import { useEffect, useState } from 'react';
import { CampoDaJanela, Janela } from '../../common/interface';
import { useGruposPassageiros } from './useGruposPassageiros';
import type { PassageiroGrupo } from './useGruposPassageiros';

interface Props {
  aberto: boolean;
  /** Os passageiros que virarão o grupo — de uma ATF ou do wizard. */
  passageiros: PassageiroGrupo[];
  /** Sugestão de nome; o cooperado pode trocar. */
  nomeSugerido?: string;
  onFechar: () => void;
  onSalvo?: (idGrupo: number) => void;
}

/**
 * Salvar uma lista de passageiros como grupo reutilizável.
 *
 * O grupo vai para as mesmas linhas que o aplicativo lê — por isso a
 * normalização do formato fica no servidor, não aqui.
 */
export function GrupoPassageirosDialog({ aberto, passageiros, nomeSugerido, onFechar, onSalvo }: Props) {
  const { criar } = useGruposPassageiros();
  const [nome, setNome] = useState(nomeSugerido ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => { if (aberto) { setNome(nomeSugerido ?? ''); setErro(null); } }, [aberto, nomeSugerido]);

  // Só passageiro com nome vira grupo — o servidor faz o mesmo, mas a tela
  // precisa contar certo antes de enviar.
  const validos = passageiros.filter(p => p.nome?.trim());
  const podeSalvar = nome.trim().length > 0 && validos.length > 0 && !salvando;

  async function salvar() {
    if (!podeSalvar) return;
    setSalvando(true);
    setErro(null);
    try {
      const g = await criar(nome.trim(), validos);
      onSalvo?.(g.idGrupo);
      onFechar();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível salvar o grupo.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Janela
      aberta={aberto}
      titulo="Salvar como grupo"
      contexto={`${validos.length} passageiro${validos.length === 1 ? '' : 's'}, para reaproveitar na próxima emissão.`}
      apoio="O grupo também aparece no aplicativo."
      erro={erro}
      ocupado={salvando}
      onFechar={onFechar}
      acoes={<>
        <button type="button" className="co-btn co-btn-secundario" onClick={onFechar} disabled={salvando}>Voltar</button>
        <button type="button" className="co-btn co-btn-primario" onClick={salvar} disabled={!podeSalvar}>
          {salvando ? 'Salvando…' : 'Salvar grupo'}
        </button>
      </>}
    >
      <CampoDaJanela rotulo="Nome do grupo" largo>
        <input
          value={nome}
          onChange={e => setNome(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') salvar(); }}
          placeholder="Ex.: Turma da manhã"
          autoFocus
        />
      </CampoDaJanela>

      {validos.length > 0 && (
        <div className="co-etiquetas">
          {validos.slice(0, 6).map((p, i) => <span key={i} className="co-etiqueta">{p.nome}</span>)}
          {validos.length > 6 && <span className="co-apoio">+{validos.length - 6}</span>}
        </div>
      )}
    </Janela>
  );
}
